import {
  deriveAccountsDefinition,
  deriveClientAddress,
  deriveServiceTypeDescription,
  deriveServiceTypeSupportCoordinationParams,
} from '~appointments/lib/derive';
import { initialiseUserOption } from '~appointments/lib/initialise';
import { onChangeClient, onChangePackageHandler, onChangeServiceType } from '~appointments/lib/onchange';
import { showLocations } from '~appointments/lib/show';
import { validateCareWorker, validateStartEndTimes } from '~appointments/lib/validate';

import { FieldProps } from '~components/form-fields';
import FormInputCareWorkers from '~appointments/components/FormInputCareWorkers';
import FormInputDate from '~appointments/components/FormInputDate';
import FormInputNDISRates from '~appointments/components/FormInputNDISRates';
import FormInputPackageSelector from '~appointments/components/FormInputPackageSelector';
import FormInputStartEndTimes from '~appointments/components/FormInputStartEndTimes';

import { getAll as getServiceTypes } from '~appointments/actions/serviceTypes';
import { getAll as getClients } from '~main/actions/clients';

import { selectLocations } from '~appointments/selectors/locations';
import { selectAppointmentServiceTypes } from '~appointments/selectors/serviceTypes';
import { selectAppointmentStatuses } from '~appointments/selectors/statuses';
import { selectLookupUsers } from '~care-workers/selectors/users';
import { selectLookupClients } from '~main/selectors/clients/clients';

const newSupportCoordinationFields: FieldProps = {
  id: {
    type: 'hidden',
  },
  date: {
    type: 'custom',
    component: FormInputDate,
    caption: 'Appointment Date',
    required: true,
    colSize: 4,
    order: 100,
  },
  client_name: {
    type: 'select',
    field: 'client_id',
    caption: 'Client / Facility',
    deriveDescription: deriveClientAddress,
    onChange: onChangeClient,
    className: 'text-left',
    required: true,
    colSize: 4,
    order: 200,
    selectorOptions: selectLookupClients,
    selectorActions: getClients,
    manualReload: true,
  },

  package_fixed_id: {
    type: 'custom',
    component: FormInputPackageSelector,
    dependencies: ['package_fixed_id', 'client_id', 'date'],
    onChange: onChangePackageHandler(true),
    required: true,
    colSize: 8,
    order: 300,
  },
  location_override_id: {
    type: 'select',
    caption: 'Location',
    colSize: 4,
    description: 'Optional, defaults to client home address',
    order: 400,
    show: showLocations,
    selectorOptions: selectLocations,
    dataCy: 'location-id-select',
  },
  service_type_id: {
    type: 'select-lazy',
    caption: 'Service Type',
    selectorOptions: selectAppointmentServiceTypes,
    selectorActions: getServiceTypes,
    selectorParams: deriveServiceTypeSupportCoordinationParams,
    deriveWarning: deriveServiceTypeDescription,
    onChange: onChangeServiceType,
    order: 500,
    colSize: 4,
    required: false,
    dataCy: 'service-type-id-select',
  },
  accounts_definitions: {
    type: 'custom',
    component: FormInputNDISRates,
    deriveValue: deriveAccountsDefinition,
    dependencies: ['client_id', 'service_type_id', 'accounts_definitions'],
    order: 600,
  },
  user_id: {
    type: 'custom',
    caption: 'Support Coordinator',
    component: FormInputCareWorkers,
    validateConfirm: validateCareWorker,
    initialiseInitialValue: initialiseUserOption,
    selectorOptions: selectLookupUsers,
    dependencies: ['client_id', 'user_id', 'date', 'id', 'support_coordination', 'case_management'],
    colSize: 6,
    order: 700,
  },
  start_end_time: {
    type: 'custom',
    caption: 'Start/End Times',
    colSize: 6,
    component: FormInputStartEndTimes,
    dependencies: ['date', 'start_time', 'end_time', 'ends_next_day'],
    validate: validateStartEndTimes,
    order: 800,
  },
  notes: {
    type: 'textarea',
    caption: 'Notes',
    className: 'note-text-area',
    placeholder: 'Note will be added to client in CRM',
    required: true,
    order: 900,
  },
  status_id: {
    type: 'readonly',
    caption: 'Marked as',
    description: 'Status will be set on creation',
    dataCy: 'status_id',
    selectorOptions: selectAppointmentStatuses,
    order: 1000,
  },
};

export default newSupportCoordinationFields;
